import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { FormSectionWithRows } from '@strata/react-form-section';
import { NumberInputFormik } from '@strata/react-inputs';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { postExecuteBatch, postPreviewBatch } from 'api/oopsAPI';
import { handleError } from 'services/Error';
import { useGetPayerSegmentListQuery } from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    payer_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    low_touch_referrals: Yup.number().required(validationStrings.requiredField.errorMessage),
    high_touch_referrals: Yup.number().required(validationStrings.requiredField.errorMessage),
    execute: Yup.bool().required(validationStrings.requiredField.errorMessage),
    raw_data: Yup.bool().required(validationStrings.requiredField.errorMessage),
});

export const Batch = () => {
    const [generatedBatch, setGeneratedBatch] = useState<object>();

    const { data: payerSegmentList, isFetching: payerSegmentListFetching } =
        useGetPayerSegmentListQuery();

    return (
        <Formik
            initialValues={
                {
                    execute: false,
                    raw_data: false,
                } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    if (values.execute) {
                        const response = await postExecuteBatch(values);
                        SuccessToast({ message: 'Batch Referrals Created.' });
                        setGeneratedBatch(response);
                    } else {
                        const response = await postPreviewBatch(values);
                        SuccessToast({ message: 'Preview Batch Referrals.' });
                        setGeneratedBatch(response);
                    }
                } catch (err) {
                    handleError('Error configuring batch referrals.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Configure Batch Referrals"
                    primaryButtonLabel="Create Batch Referrals"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="segment_id"
                                label="Payer Segment"
                                required
                                isLoading={payerSegmentListFetching}
                                values={payerSegmentList}
                                helperText="Payer segment to manage routes for"
                                selectedItem={payerSegmentList?.find(
                                    ({ segmentId }) => segmentId === formik.values.segment_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'segment_id',
                                            newValue?.segmentId,
                                        );
                                        await formik.setFieldValue('payer_id', newValue?.payerId);
                                    })();
                                }}
                            />
                            <NumberInputFormik
                                id="low_touch_referrals"
                                label="Low Touch Referrals"
                                required
                            />
                            <NumberInputFormik
                                id="high_touch_referrals"
                                label="High Touch Referrals"
                                required
                            />
                            <FormSectionWithRows
                                sectionTitle="Batch Options"
                                width="100%"
                                rowOneContent={
                                    <>
                                        <CheckboxListFormik
                                            name="execute"
                                            options={['Execute']}
                                            values={formik.values.execute ? ['Execute'] : []}
                                            onChange={(newVal) => {
                                                void formik.setFieldValue(
                                                    'execute',
                                                    newVal?.length === 1,
                                                );
                                            }}
                                        />
                                        <CheckboxListFormik
                                            name="raw_data"
                                            options={['Raw Data']}
                                            values={formik.values.raw_data ? ['Raw Data'] : []}
                                            onChange={(newVal) => {
                                                void formik.setFieldValue(
                                                    'raw_data',
                                                    newVal?.length === 1,
                                                );
                                            }}
                                        />
                                    </>
                                }
                            />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Creating Batch Referrals:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>
                                    {generatedBatch && JSON.stringify(generatedBatch, null, 2)}
                                </pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
